import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import PlugIcon from "@mui/icons-material/CableRounded";
import ScienceIcon from "@mui/icons-material/ScienceOutlined";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMoveOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import ArchiveIcon from "@mui/icons-material/Inventory2Outlined";
import { EditDetailsDialog } from "@/components/common/edit-details-dialog";
import { EntityActionsMenu } from "@/components/common/entity-actions-menu";
import { MoveToWorkspaceDialog } from "@/components/common/move-to-workspace-dialog";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { Breadcrumbs } from "@/components/nav/breadcrumbs";
import { ConnectorIcon } from "@/components/connectors/connector-icon";
import { SchemaForm } from "@/components/connectors/schema-form";
import { DbPlayground } from "@/components/connectors/db-playground";
import { DryRunDialog } from "@/components/connectors/dry-run-dialog";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { IdChip } from "@/components/display/mono";
import { LoadingState, ErrorState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { useUndoableDelete } from "@/lib/undoable-delete";
import { useWorkspaces } from "@/api/workspaces";
import {
  connectorQuery,
  useConnector,
  useConnectorMutations,
  useConnectorTypes,
} from "@/api/connectors";

export const Route = createFileRoute("/_app/connectors/$connectorId")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(connectorQuery(params.connectorId)),
  component: ConnectorDetail,
});

function ConnectorDetail() {
  const { connectorId } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const undoableDelete = useUndoableDelete<{ id: string; name: string }>({
    noun: "Connector",
    key: (i) => i.id,
    name: (i) => i.name,
    mutate: (i) => m.remove.mutateAsync(i.id),
    invalidate: [["connectors"]],
  });
  const c = useConnector(connectorId);
  const types = useConnectorTypes();
  const m = useConnectorMutations(connectorId);
  const { data: workspaces } = useWorkspaces();

  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [dirty, setDirty] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [tab, setTab] = useState(0);
  const [dryRun, setDryRun] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);

  const loadedConfig = c.data?.config;
  const loadedId = c.data?.id;
  useEffect(() => {
    setConfig({ ...(loadedConfig ?? {}) });
    setDirty(false);
    // Re-seed only when a different connector loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedId]);

  if (c.isLoading) return <LoadingState />;
  if (c.error || !c.data)
    return <ErrorState error={c.error ?? "Not found"} onRetry={() => c.refetch()} />;

  const typeMeta = types.data?.find((t) => t.type === c.data!.type);
  const introspectable = Boolean(typeMeta?.introspectable);

  async function onSave() {
    await m.update.mutateAsync({ name: c.data!.name, type: c.data!.type, config });
    setDirty(false);
    toast.success("Connector saved");
  }

  async function onTest() {
    setTestResult(null);
    try {
      const res = await m.test.mutateAsync(connectorId);
      setTestResult({
        ok: Boolean(res.success),
        message: res.message ?? (res.success ? "Connected" : "Failed"),
      });
    } catch (e) {
      setTestResult({ ok: false, message: e instanceof Error ? e.message : "Test failed" });
    }
  }

  function onDelete() {
    undoableDelete.remove(
      { id: connectorId, name: c.data!.name },
      { onUndo: () => navigate({ to: "/connectors/$connectorId", params: { connectorId } }) },
    );
    navigate({ to: "/connectors" });
  }

  return (
    <PageContainer>
      <PageHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ConnectorIcon type={c.data.type} />
            {c.data.name}
          </Box>
        }
        breadcrumbs={
          <Breadcrumbs
            items={[{ label: "Connectors", to: "/connectors" }, { label: c.data.name }]}
          />
        }
        actions={
          <>
            <Button startIcon={<PlugIcon />} onClick={onTest} disabled={m.test.isPending}>
              Test
            </Button>
            <Button startIcon={<ScienceIcon />} onClick={() => setDryRun(true)}>
              Dry run
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!dirty || m.update.isPending}
              onClick={onSave}
            >
              Save
            </Button>
            <EntityActionsMenu
              items={[
                {
                  label: "Edit details",
                  icon: <DriveFileRenameOutlineIcon fontSize="small" />,
                  onClick: () => setEditOpen(true),
                },
                {
                  label: "Move to workspace…",
                  icon: <DriveFileMoveIcon fontSize="small" />,
                  onClick: () => setMoveOpen(true),
                },
                {
                  label: "Duplicate",
                  icon: <ContentCopyIcon fontSize="small" />,
                  onClick: async () => {
                    const dup = await m.duplicate.mutateAsync(connectorId);
                    toast.success("Connector duplicated");
                    navigate({ to: "/connectors/$connectorId", params: { connectorId: dup.id } });
                  },
                },
                {
                  label: c.data.archivedAt ? "Unarchive" : "Archive",
                  icon: <ArchiveIcon fontSize="small" />,
                  divider: true,
                  onClick: async () => {
                    await m.setArchived.mutateAsync({
                      cid: connectorId,
                      archived: !c.data!.archivedAt,
                    });
                    toast.success(c.data!.archivedAt ? "Unarchived" : "Archived");
                  },
                },
                {
                  label: "Delete",
                  icon: <DeleteIcon fontSize="small" />,
                  onClick: onDelete,
                  danger: true,
                },
              ]}
            />
          </>
        }
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: "center", flexWrap: "wrap" }}>
        <StatusChip status={c.data.status ?? "UNKNOWN"} />
        <Chip size="small" variant="outlined" label={c.data.type} />
        {c.data.kind ? <Chip size="small" variant="outlined" label={c.data.kind} /> : null}
        {c.data.archivedAt ? <Chip size="small" label="Archived" /> : null}
        {dirty ? <Chip size="small" color="warning" label="Unsaved" /> : null}
      </Stack>

      {testResult ? (
        <Alert severity={testResult.ok ? "success" : "error"} sx={{ mb: 2 }}>
          {testResult.message}
        </Alert>
      ) : null}

      {introspectable ? (
        <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Configuration" />
          <Tab label="DB playground" />
        </Tabs>
      ) : null}

      {tab === 1 ? (
        <DbPlayground connectorId={connectorId} />
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 3,
            alignItems: "start",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 320px" },
          }}
        >
          <Card sx={{ p: 3 }}>
            <SchemaForm
              schema={typeMeta?.formSchema}
              value={config}
              onChange={(next) => {
                setConfig(next);
                setDirty(true);
              }}
              secretFields={typeMeta?.secretFields ?? []}
            />
            {!typeMeta ? (
              <Typography variant="body2" color="text.secondary">
                No form schema for type <strong>{c.data.type}</strong>.
              </Typography>
            ) : null}
          </Card>

          <Card sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Details
            </Typography>
            <Stack spacing={1.25}>
              <Meta label="Type" value={c.data.type} />
              <Meta label="Kind" value={c.data.kind ?? "STANDALONE"} />
              <Meta
                label="Workspace"
                value={workspaces?.find((w) => w.id === c.data!.workspaceId)?.name ?? "—"}
              />
              <Meta label="Description" value={c.data.description || "—"} />
              <Meta label="Last tested" value={<RelativeTime value={c.data.lastTestedAt} />} />
              <Meta label="Updated" value={<RelativeTime value={c.data.updatedAt} />} />
              <Meta label="ID" value={<IdChip value={connectorId} truncate={14} />} />
            </Stack>
          </Card>
        </Box>
      )}

      <DryRunDialog
        open={dryRun}
        onClose={() => setDryRun(false)}
        connectorId={connectorId}
        connectorName={c.data.name}
      />

      <EditDetailsDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        noun="connector"
        initial={{ name: c.data.name, description: c.data.description ?? "" }}
        onSave={(v) => m.update.mutateAsync({ name: v.name, description: v.description })}
      />

      <MoveToWorkspaceDialog
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        noun="connector"
        currentWorkspaceId={c.data.workspaceId}
        onMove={(workspaceId) => m.moveWorkspace.mutateAsync({ cid: connectorId, workspaceId })}
      />
    </PageContainer>
  );
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {value}
      </Typography>
    </div>
  );
}
